import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";
import api, { fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { Star, MapPin, Clock, ShieldCheck } from "lucide-react";
import { AvailabilityDisplay } from "@/components/AvailabilityEditor";
import { BusyMonthGrid } from "@/components/BusyMonthGrid";

function BusyBlocks({ blocks }) {
  if (!blocks || blocks.length === 0) return null;
  const upcoming = blocks
    .filter((b) => b.end && new Date(b.end) > new Date())
    .sort((a, b) => new Date(a.start) - new Date(b.start))
    .slice(0, 8);
  if (upcoming.length === 0) return null;
  return (
    <div className="mt-10 border-t hairline pt-8" data-testid="busy-blocks">
      <div className="tick-label">Blocked by host calendar</div>
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
        {upcoming.map((b, i) => (
          <div key={i} className="border border-[#FF3B30]/50 bg-[#FF3B30]/5 px-3 py-2 font-mono text-xs flex items-center justify-between">
            <span>{new Date(b.start).toLocaleString()}</span>
            <span className="text-[#FF3B30]">→ {new Date(b.end).toLocaleString()}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function KitchenDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [k, setK] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [conflict, setConflict] = useState(null);
  const [insurance, setInsurance] = useState(false);

  useEffect(() => {
    api.get(`/kitchens/${id}`).then((r) => setK(r.data)).catch(() => nav("/browse"));
    api.get(`/reviews/kitchen/${id}`).then((r) => setReviews(r.data));
  }, [id, nav]);

  if (!k) return (<><Navbar /><div className="max-w-7xl mx-auto px-6 py-20 tick-label">Loading...</div></>);

  const hours = start && end ? Math.max(0, (new Date(end) - new Date(start)) / 3600000) : 0;
  const subtotal = hours * k.hourly_rate;
  const fee = subtotal * 0.15;
  const insuranceFee = insurance ? 9 : 0;
  const total = subtotal + fee + insuranceFee;

  const book = async () => {
    if (!user) { nav("/login"); return; }
    if (user.role !== "cook") { toast.error("Only cooks can book"); return; }
    if (!user.license_verified) { toast.error("Upload your food handler license first"); nav("/cook"); return; }
    setSubmitting(true);
    setConflict(null);
    try {
      const iso = (s) => new Date(s).toISOString();
      const { data } = await api.post("/bookings", {
        kitchen_id: k.id,
        start_time: iso(start),
        end_time: iso(end),
        notes,
        insurance,
      });
      toast.success(data.status === "approved" ? "Booking approved — proceed to pay" : "Booking sent for approval");
      nav("/cook");
    } catch (err) {
      const detail = err.response?.data?.detail || "Booking failed";
      if (detail.includes("already booked") || detail.includes("external calendar")) {
        setConflict({ detail, start, end });
      } else {
        toast.error(detail);
      }
    } finally { setSubmitting(false); }
  };

  const joinWaitlist = async () => {
    if (!user || user.role !== "cook") return;
    try {
      const iso = (s) => new Date(s).toISOString();
      await api.post("/waitlist", {
        kitchen_id: k.id,
        start_time: iso(start),
        end_time: iso(end),
      });
      toast.success("On the waitlist — we'll email you the second this slot opens");
      setConflict(null);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const photos = k.photos?.length ? k.photos : [];

  return (
    <div className="min-h-screen">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 py-10">
        {/* GALLERY */}
        <div className="grid grid-cols-4 gap-2 border hairline">
          <div className="col-span-4 md:col-span-3 aspect-[16/9] bg-neutral-900 overflow-hidden">
            <img
              src={photos[0] ? fileUrl(photos[0]) : "https://images.unsplash.com/photo-1782750161991-23529c9462bb?crop=entropy&cs=srgb&fm=jpg&q=85"}
              alt={k.name}
              className="w-full h-full object-cover"
            />
          </div>
          <div className="col-span-4 md:col-span-1 grid grid-cols-2 md:grid-cols-1 gap-2">
            {photos.slice(1, 4).map((p, i) => (
              <div key={i} className="aspect-square bg-neutral-900 overflow-hidden">
                <img src={fileUrl(p)} alt="" className="w-full h-full object-cover" />
              </div>
            ))}
          </div>
        </div>

        <div className="mt-10 grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2">
            <div className="tick-label">{k.city}{k.state ? `, ${k.state}` : ""}</div>
            <h1 data-testid="kitchen-title" className="font-display font-black text-5xl lg:text-6xl tracking-tighter mt-2">{k.name}</h1>
            <div className="mt-4 flex items-center gap-6 text-sm text-neutral-400">
              <span className="flex items-center gap-1"><MapPin size={14} /> {k.address}</span>
              {k.review_count > 0 && (
                <span className="flex items-center gap-1"><Star size={14} className="fill-[#FF3B30] text-[#FF3B30]" /> {k.rating.toFixed(1)} ({k.review_count})</span>
              )}
              <span className="flex items-center gap-1"><Clock size={14} /> Min {k.min_hours}h</span>
            </div>

            <div className="mt-10 border-t hairline pt-8">
              <div className="tick-label">About this kitchen</div>
              <p className="mt-4 text-neutral-300 leading-relaxed whitespace-pre-wrap">{k.description}</p>
            </div>

            <div className="mt-10 border-t hairline pt-8">
              <div className="tick-label">Weekly availability</div>
              <AvailabilityDisplay value={k.availability} />
            </div>

            <BusyBlocks blocks={k.external_busy} />
            <BusyMonthGrid
              blocks={k.external_busy}
              onDayClick={(d) => {
                // format for <input type=datetime-local>
                const pad = (n) => String(n).padStart(2, "0");
                const iso = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
                setStart(iso);
                document.querySelector('[data-testid="booking-start"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
              }}
            />

            {k.amenities?.length > 0 && (
              <div className="mt-10 border-t hairline pt-8">
                <div className="tick-label">Amenities</div>
                <div className="mt-4 flex flex-wrap gap-2">
                  {k.amenities.map((a, i) => (
                    <span key={i} className="border hairline px-3 py-1.5 text-sm">{a}</span>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-10 border-t hairline pt-8">
              <div className="tick-label">Reviews</div>
              {reviews.length === 0 ? (
                <p className="mt-4 text-neutral-500">No reviews yet.</p>
              ) : (
                <div className="mt-6 space-y-6">
                  {reviews.map((r) => (
                    <div key={r.id} className="border-b hairline pb-6">
                      <div className="flex items-center justify-between">
                        <span className="font-semibold">{r.cook_name}</span>
                        <span className="flex gap-0.5">
                          {[1,2,3,4,5].map(i => (
                            <Star key={i} size={12} className={i<=r.rating?"fill-[#FF3B30] text-[#FF3B30]":"text-neutral-700"} />
                          ))}
                        </span>
                      </div>
                      <p className="mt-2 text-neutral-400 text-sm">{r.comment}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* BOOKING PANEL */}
          <aside className="lg:col-span-1">
            <div className="border hairline p-6 sticky top-24" data-testid="booking-panel">
              <div className="flex items-baseline justify-between">
                <span className="font-mono text-4xl font-bold">${k.hourly_rate}</span>
                <span className="tick-label">per hr</span>
              </div>
              <div className="tick-label mt-2 flex items-center gap-2">
                <ShieldCheck size={12} /> {k.booking_mode === "instant" ? "Instant book" : "Host approval required"}
              </div>

              <div className="mt-6 space-y-3">
                <div>
                  <div className="tick-label mb-1">Start</div>
                  <input
                    data-testid="booking-start"
                    type="datetime-local"
                    className="w-full border hairline px-3 py-2.5 focus:outline-none focus:border-[#FF3B30]"
                    value={start}
                    onChange={(e) => setStart(e.target.value)}
                  />
                </div>
                <div>
                  <div className="tick-label mb-1">End</div>
                  <input
                    data-testid="booking-end"
                    type="datetime-local"
                    className="w-full border hairline px-3 py-2.5 focus:outline-none focus:border-[#FF3B30]"
                    value={end}
                    onChange={(e) => setEnd(e.target.value)}
                  />
                </div>
                <div>
                  <div className="tick-label mb-1">Notes to host</div>
                  <textarea
                    data-testid="booking-notes"
                    className="w-full border hairline px-3 py-2.5 focus:outline-none focus:border-[#FF3B30] min-h-[80px]"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                  />
                </div>
              </div>

              <div className="mt-6 space-y-2 font-mono text-sm border-t hairline pt-4">
                <div className="flex justify-between"><span className="text-neutral-500">Hours</span><span>{hours.toFixed(2)}</span></div>
                <div className="flex justify-between"><span className="text-neutral-500">Subtotal</span><span>${subtotal.toFixed(2)}</span></div>
                <div className="flex justify-between"><span className="text-neutral-500">Platform 15%</span><span>${fee.toFixed(2)}</span></div>
                {insurance && (
                  <div className="flex justify-between text-[#FF3B30]"><span>Damage cover</span><span>+${insuranceFee.toFixed(2)}</span></div>
                )}
                <div className="flex justify-between text-lg font-bold pt-2 border-t hairline"><span>Total</span><span data-testid="booking-total">${total.toFixed(2)}</span></div>
              </div>

              <label className="mt-4 flex items-start gap-3 cursor-pointer" data-testid="insurance-toggle">
                <input
                  type="checkbox"
                  data-testid="insurance-checkbox"
                  checked={insurance}
                  onChange={(e) => setInsurance(e.target.checked)}
                  className="mt-1 accent-[#FF3B30] w-4 h-4"
                />
                <span className="text-sm">
                  <span className="font-semibold">Add damage cover — $9</span>
                  <span className="block text-neutral-500 text-xs mt-0.5">Optional protection for accidental damage during your shift. Recommended for equipment-heavy kitchens.</span>
                </span>
              </label>

              <button
                data-testid="booking-submit"
                onClick={book}
                disabled={submitting || hours <= 0}
                className="btn-signal w-full mt-6 disabled:opacity-50"
              >
                {submitting ? "..." : k.booking_mode === "instant" ? "Book & pay next" : "Request booking"}
              </button>
              {conflict && (
                <div data-testid="conflict-waitlist" className="mt-4 border hairline p-4 bg-neutral-950">
                  <div className="tick-label text-[#FF3B30]">Slot taken</div>
                  <p className="text-sm text-neutral-300 mt-1">{conflict.detail}</p>
                  <button
                    data-testid="join-waitlist-btn"
                    onClick={joinWaitlist}
                    className="btn-outline mt-3 w-full"
                  >Join waitlist — get first dibs if it opens</button>
                </div>
              )}
              {!user && <Link to="/login" className="tick-label text-center block mt-3 text-[#FF3B30]">Sign in to book</Link>}
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
